import type { ColumnDef, ColumnDefTemplate, HeaderContext } from '@tanstack/react-table';
import type { DataGridColumnDef, DataGridColumnDefOptions, DataGridOptions } from '../../data-grid.type';

import { getColumnDefCell } from '../../util/cell';
import { getColumnDefHeader } from '../../util/header';
import { createActionColumn } from './actions';

interface DataGridColumnCreateOptions {
	hasActionColumn?: boolean;
	hasSelectionColumn?: boolean;
	options: DataGridOptions;
}

export interface DataGridColumnCreateReturn<T> {
	filterableColumns: DataGridColumnDef<T>[];
	realColumns: ColumnDef<T>[]; // The created columns for the DataGrid (Tanstack Table)
}

const DEFAULT_CREATE_COLUNMS_OPTIONS: Omit<DataGridColumnCreateOptions, 'options'> = {
	hasActionColumn: false,
	hasSelectionColumn: false,
};

/**
 * createColumns - Create columns for the DataGrid (Tanstack Table) from the DataGridColumnDef
 *
 * @param {DataGridColumnDef<T>[]} columns - The columns to create
 * @returns {DataGridColumnCreateReturn}
 */
export function createColumns<T>(
	columns: DataGridColumnDef<T>[],
	{
		hasSelectionColumn = DEFAULT_CREATE_COLUNMS_OPTIONS.hasSelectionColumn,
		options: dataGridOptions,
	}: DataGridColumnCreateOptions
): DataGridColumnCreateReturn<T> {

	const realColumns = columns
		.filter(col => col.type !== 'filter-only')
		.map((col, i): ColumnDef<T> | null => {
		if (col.type === 'actions') {
			return createActionColumn(col);
		}

		const isSelectionColumn = hasSelectionColumn && i === 0;

		const columnDef: Partial<ColumnDef<T>> = {
			id: col.key,
			accessorKey: col.key,
			enableHiding: !col.hidden,
			enableResizing: isSelectionColumn ? false : col.resizable ?? false,
			enableSorting: col.sortable ?? true,
			...(typeof col.width === 'number' ? { size: col.width, meta: { hasExplicitWidth: true } } : {}),
		};

		// The `flexRender` passes additional context to the header and cell renderers
		columnDef.header = getColumnDefHeader(col) as ColumnDefTemplate<HeaderContext<T, unknown>>;
		
		const options: DataGridColumnDefOptions = {
			...dataGridOptions,
			finOptions: {
				...dataGridOptions.finOptions,
				...col.finOptions,
			},
			parse: col.parse,
		};

    columnDef.cell = getColumnDefCell(col, options);

			return columnDef as ColumnDef<T>;
		}).filter((col): col is ColumnDef<T> => col !== null);

	return {
		filterableColumns: columns.filter(col => col.filter),
		realColumns,
	}
}
